import { Container, Heading } from '@seek/ai-elements';
import { useTranslations } from '@vocab/react';
import { Box, IconAI, Stack } from 'braid-design-system';
import { useMemo, type ComponentProps } from 'react';

import SkeletonLine from 'src/components/SkeletonLine/SkeletonLine';

import { useSignedInDashboardContext } from '../SignedInDashboardContextProvider';

import translations from './.vocab';
import { CareerFeedAIAssistantContent } from './CareerFeedAIAssistantContent/CareerFeedAIAssistantContent';
import { useCareerFeedAIAssistant } from './hooks/useCareerFeedAIAssistant';

const useHeading = () => {
  const { progress } = useCareerFeedAIAssistant();
  const { t } = useTranslations(translations);

  return useMemo(() => {
    switch (progress) {
      case 'initial':
        return t('Get better job matches');
      case 'gatheringRefinement':
        return t('Define your preferences');
      case 'reviewRefinement':
        return t('Review you preferences');
      case 'recsRefined':
      case 'recsRefined-withPersistedData':
        return t('Refined for you');
    }
  }, [progress, t]);
};

const CareerFeedAIAssistantContainer = ({
  showHeading,
  children,
}: {
  showHeading?: boolean;
  children: ComponentProps<typeof Stack>['children'];
}) => {
  const headingText = useHeading();

  return (
    <Box display="flex" flexDirection="column" width="full">
      <Container borderWidth="standard" padding="medium">
        <Stack space="medium">
          {showHeading && (
            <Heading level="4" icon={<IconAI />}>
              {headingText}
            </Heading>
          )}
          {children}
        </Stack>
      </Container>
    </Box>
  );
};

/** *
 * This component is responsible for displaying the AI Assistant for the Career Feed.
 * This is still in in discovery phase and the implementation of this component might change drastically.
 * The reason why this is in the code base is so that people can start playing around with it
 * and get feedback on the implementation.
 */
export const CareerFeedAIAssistant = () => {
  const {
    recommendationsResult: {
      error: isRecommendationError,
      loading: isRecommendationLoading,
      isRefreshLoading,
    },
  } = useSignedInDashboardContext();

  if (isRecommendationError) {
    return null;
  }

  const isLoadingState = isRecommendationLoading && !isRefreshLoading;

  return (
    <CareerFeedAIAssistantContainer showHeading={!isLoadingState}>
      {isLoadingState ? (
        <Stack space="medium">
          <SkeletonLine type="text" width={200} size="small" />
          <SkeletonLine type="text" width={260} size="xsmall" />
        </Stack>
      ) : (
        <CareerFeedAIAssistantContent />
      )}
    </CareerFeedAIAssistantContainer>
  );
};
